import React, { useMemo } from 'react';
import { MainScreenLayout } from 'core/models/layout';
import { CameraSettingFormGroup } from 'components/views/molecules/CameraSettingFormGroup';
import styled from '@emotion/styled';
import { fontFamily } from 'components/views/default';
import { MicrophoneSettingFormGroup } from 'components/views/molecules/MicrophoneSettingFormGroup';
import { MediaDevice, CameraDevice, MicrophoneDevice } from 'core/models/mediaDevice';

const Frame = styled.div({
    minWidth: '30em',
});

const ScreenName = styled.div({
    fontFamily,
    fontSize: 'x-large',
});

export type MainScreenLayoutSettingPanelProps = {
    className?: string;
    cameraDevices: CameraDevice[] | undefined;
    microphoneDevices: MicrophoneDevice[] | undefined;
    layout: MainScreenLayout;
    onUpdate: (layout: MainScreenLayout) => void;
    getVideoConfig?: (mediaDeviceId: MediaDevice.Id) => Promise<Partial<MediaDevice.VideoConfig>>;
    putVideoConfig?: (mediaDeviceId: MediaDevice.Id, config: Partial<MediaDevice.VideoConfig>) => Promise<void>;
    getAudioConfig?: (mediaDeviceId: MediaDevice.Id) => Promise<Partial<MediaDevice.AudioConfig>>;
    putAudioConfig?: (mediaDeviceId: MediaDevice.Id, config: Partial<MediaDevice.AudioConfig>) => Promise<void>;
}

export function MainScreenLayoutSettingPanel(props: MainScreenLayoutSettingPanelProps) {
    const { onUpdate, layout, cameraDevices, microphoneDevices, getVideoConfig, putVideoConfig, getAudioConfig, putAudioConfig } = props;

    function makeDuplicateList(layout: MainScreenLayout) {
        const countByPosition =
            [...layout.center, ...layout.top, ...layout.left, ...layout.right]
                .reduce((s, c) => s.set(c.tag, (s.get(c.tag) ?? 0) + 1), new Map<MediaDevice.Tag | undefined, number>());
        return new Set([...countByPosition.entries()].reduce((a, [k, n]) => (k && n > 1) ? [...a, k] : a, [] as MediaDevice.Tag[]));
    }
    const duplicateCameras = useMemo(() => makeDuplicateList(layout), [layout]);

    function onCheckedUpdate(layout: MainScreenLayout) {
        onUpdate(layout);
    }

    return (
        <Frame className={props.className}>
            <ScreenName>Main Display</ScreenName>
            <CameraSettingFormGroup groupName='Center' keyBase='center'
                min={1} max={3}
                assignedCameras={layout.center}
                duplicateCameras={duplicateCameras}
                cameraDevices={cameraDevices}
                onChange={main => onCheckedUpdate({ ...layout, center: main })}
                getVideoConfig={getVideoConfig}
                putVideoConfig={putVideoConfig}
            />
            <CameraSettingFormGroup groupName='Top' keyBase='top'
                assignedCameras={layout.top}
                duplicateCameras={duplicateCameras}
                cameraDevices={cameraDevices}
                onChange={top => onCheckedUpdate({ ...layout, top })}
                getVideoConfig={getVideoConfig}
                putVideoConfig={putVideoConfig}
            />
            <CameraSettingFormGroup groupName='Left' keyBase='left'
                assignedCameras={layout.left}
                duplicateCameras={duplicateCameras}
                cameraDevices={cameraDevices}
                onChange={left => onCheckedUpdate({ ...layout, left })}
                getVideoConfig={getVideoConfig}
                putVideoConfig={putVideoConfig}
            />
            <CameraSettingFormGroup groupName='Right' keyBase='right'
                assignedCameras={layout.right}
                duplicateCameras={duplicateCameras}
                cameraDevices={cameraDevices}
                onChange={right => onCheckedUpdate({ ...layout, right })}
                getVideoConfig={getVideoConfig}
                putVideoConfig={putVideoConfig}
            />
            <MicrophoneSettingFormGroup groupName='Microphone' keyBase='mic' min={1} max={1}
                assignedMicrophones={layout.microphone ? [layout.microphone] : []}
                duplicateMicrophones={duplicateCameras}
                mediaDevices={microphoneDevices}
                onChange={microphone => onCheckedUpdate({ ...layout, microphone: microphone[0] })}
                getAudioConfig={getAudioConfig}
                putAudioConfig={putAudioConfig}
            />
        </Frame>
    );
}
